import { Button, Table, addToast, showDialogConfirm } from '@octano/global-ui';
import { useCallback, useState } from 'react';
import { SectionTitle } from '../../../../components/text';
import { Base } from '../../types';
import { notifyDegreeDocuments } from '../../requests';
import { useColumns } from './hooks/useColumns';

type Props = {
  studyPlanEnrollmentId: number;
  documents: Base['degreeDocuments'];
  texts: {
    title: string;
    notifyButton?: string;
    notify?: {
      confirm: {
        title: string;
        description: string;
        confirm: string;
        cancel: string;
      };
      toasts: {
        success: string;
        failed: string;
      };
    };
    selectOptions: {
      rejected: string;
      approved: string;
      pending: string;
    };
    toasts: {
      upload: {
        success: string;
        failed: string;
      };
      remove: {
        success: string;
        failed: string;
      };
      editStatus: {
        success: string;
        failed: string;
      };
      confirmDelete: {
        title: string;
        description: string;
        confirm: string;
        cancel: string;
      };
    };
    table: {
      noResults: string;
      headers: {
        name: string;
        documentName: string;
        load: string;
        state: string;
      };
      values: {
        loadButton: string;
      };
    };
  };
  refresh: Function;
  disableEditing?: boolean;
};

export function DegreeDocuments({
  studyPlanEnrollmentId,
  documents,
  texts,
  refresh,
  disableEditing = false,
}: Props) {
  const columns = useColumns(
    studyPlanEnrollmentId,
    texts,
    refresh,
    disableEditing,
  );
  const [sending, setSending] = useState(false);
  const notifyTexts = texts.notify;

  const handleNotify = useCallback(async () => {
    if (!notifyTexts) {
      return;
    }
    setSending(true);
    const { error } = await notifyDegreeDocuments(studyPlanEnrollmentId);
    setSending(false);
    if (!error) {
      addToast({
        color: 'success',
        icon: 'check',
        text: notifyTexts.toasts.success,
      });
    } else {
      addToast({
        color: 'danger',
        icon: 'error',
        text: notifyTexts.toasts.failed,
      });
    }
  }, [studyPlanEnrollmentId, notifyTexts]);

  const handleRequestNotify = useCallback(() => {
    if (sending || !notifyTexts) {
      return;
    }
    showDialogConfirm({
      icon: {
        name: 'information',
        color: 'primary',
      },
      title: notifyTexts.confirm.title,
      subtitle: notifyTexts.confirm.description,
      btnConfirm: {
        text: notifyTexts.confirm.confirm,
        onConfirm: handleNotify,
      },
      btnClose: {
        text: notifyTexts.confirm.cancel,
      },
    });
  }, [sending, notifyTexts, handleNotify]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <SectionTitle text={texts.title} />
        {!!texts.notifyButton && !!notifyTexts && !!documents?.length && (
          <div style={{ flexShrink: 0 }}>
            <Button
              text={texts.notifyButton}
              size="md"
              icon="mailing"
              outlined
              loading={sending}
              disabled={sending}
              onClick={handleRequestNotify}
            />
          </div>
        )}
      </div>
      <Table
        columns={columns}
        data={documents}
        noResultsText={texts.table.noResults}
      />
    </>
  );
}
